// ./src/components/features/CardPayments/CardPaymentsNotice.tsx
//
// The "card payments" line — on the wizard's Tickets step, the quick ticket sale's screens (#148), the finish screen, the
// event dashboard's getting-started steps (#161 b) and the sign-up block editor. One component, so they cannot say
// different things about the same fact.
//
// ⚠ THE LINE IS A FACT OR NOTHING. "Card payments are on" only when Stripe answered that charges are enabled; a FAILED
//   check says it could not check — never "not set up" (it may be) and never "ready" (it may not be); before the first
//   answer, a skeleton. What "not set up" MEANS differs by screen (free tickets still work; a quick sale is kept as a
//   draft), so the caller says it (`offBody`).
import React from 'react';
import { Alert, Box, Button, Group, Skeleton, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { getRoutePath } from '@/config';
import { AppIcon } from '@/components/generic';

import type { CardPayments } from './useCardPayments';

export interface CardPaymentsNoticeProps {
  payments: CardPayments;
  /** Something on sale costs money — "not set up" is then a warning, not a note. */
  paid: boolean;
  /** The event sells in test mode: no real money moves, and no connected account is needed. */
  isTestMode?: boolean;
  /** What "not set up" means on this screen. */
  offBody: string;
  /** What a FAILED check means on this screen (default: only that it could not check). */
  unknownBody?: string;
}

/** Ask Stripe again (not the stored copy) — beside every "not set up" or "could not check" line. */
export const CheckCardPaymentsAgain: React.FC<{ payments: CardPayments }> = ({ payments }) => {
  const { t } = useTranslation(['events', 'common']);

  return (
    <Button variant="subtle" size="md" loading={payments.loading} onClick={() => void payments.check(true)}>
      {t('events:wizard.tickets.checkAgain', { defaultValue: 'Check again' })}
    </Button>
  );
};

/** Connect Stripe — in a new tab, so the screen that asked keeps its place (and asks again when the host comes back). */
export const ConnectStripeButton: React.FC = () => {
  const { t } = useTranslation(['events', 'common']);

  return (
    <Button
      component="a"
      href={getRoutePath('stripeConnect')}
      target="_blank"
      rel="noopener"
      size="md"
      variant="light"
      leftSection={<AppIcon iconName="ExternalLink" size={16} />}
    >
      {t('events:wizard.tickets.connectStripe', { defaultValue: 'Connect Stripe' })}
    </Button>
  );
};

export const CardPaymentsNotice: React.FC<CardPaymentsNoticeProps> = ({ payments, paid, isTestMode, offBody, unknownBody }) => {
  const { t } = useTranslation(['events', 'common']);
  const { checked, status } = payments;
  const checkAgain = <CheckCardPaymentsAgain payments={payments} />;

  if (isTestMode) {
    return (
      <Text size="md" c="dimmed">
        {t('events:wizard.tickets.testMode', {
          defaultValue: 'This event is in test mode — buyers pay with Stripe test cards and no real money moves.',
        })}
      </Text>
    );
  }

  if (!checked) {
    return <Skeleton height={24} width="60%" radius="sm" />;
  }

  if (status?.chargesEnabled === true) {
    return (
      <Group gap={6} align="flex-start" wrap="nowrap">
        <Box pt={4}>
          <AppIcon iconName="CircleCheck" size={16} color="var(--mantine-color-teal-6)" />
        </Box>
        <Text size="md" c="dimmed">
          {t('events:wizard.tickets.cardsReady', { defaultValue: 'Card payments are on — your Stripe account is connected.' })}
        </Text>
      </Group>
    );
  }

  if (!status) {
    // The check FAILED — unknown, which is neither "set up" nor "not set up".
    return (
      <Stack gap={4} align="flex-start">
        <Text size="md" c="dimmed">
          {unknownBody ?? t('events:wizard.tickets.cardsUnknown', { defaultValue: 'We could not check whether card payments are set up.' })}
        </Text>
        {checkAgain}
      </Stack>
    );
  }

  return (
    <Alert
      variant="light"
      color={paid ? 'orange' : 'gray'}
      icon={<AppIcon iconName="CreditCard" size={18} />}
      // ⓘ The title as a md Text: Mantine's Alert title defaults to sm (em-no-small-text).
      title={
        <Text size="md" fw={600}>
          {t('events:wizard.tickets.cardsOffTitle', { defaultValue: 'Card payments are not set up yet' })}
        </Text>
      }
    >
      <Stack gap="xs">
        <Text size="md">{offBody}</Text>
        <Group gap="sm">
          <ConnectStripeButton />
          {checkAgain}
        </Group>
      </Stack>
    </Alert>
  );
};
